import {
  AudioOutlined,
  StopOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Alert, Button, Space, Spin, Tooltip, Typography } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { apiClient } from '@/api/client';

const { Text } = Typography;

// ─── Types ───────────────────────────────────────────────

export interface VoiceRecorderProps {
  onRecordingComplete: (audioBlob: Blob, durationSeconds: number) => void;
  onTranscriptionComplete?: (text: string) => void;
  autoTranscribe?: boolean;
  /**
   * Prefer the browser's speech-recognition service when available.
   * No audio blob is uploaded or retained in this mode.
   */
  preferBrowserRecognition?: boolean;
  disabled?: boolean;
}

type RecorderState = 'idle' | 'recording' | 'processing' | 'complete' | 'error';

interface SpeechRecognitionAlternativeLike {
  transcript: string;
}

interface SpeechRecognitionResultLike {
  0: SpeechRecognitionAlternativeLike;
  isFinal: boolean;
}

interface SpeechRecognitionEventLike extends Event {
  results: {
    length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
}

interface SpeechRecognitionErrorEventLike extends Event {
  error: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | undefined {
  if (typeof window === 'undefined') return undefined;
  const recognitionWindow = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return recognitionWindow.SpeechRecognition ?? recognitionWindow.webkitSpeechRecognition;
}

const MAX_RECORD_SECONDS = 300; // 5 minutes

// ─── Waveform animation (CSS-in-JS) ──────────────────────

const waveBarStyle = (delay: string): React.CSSProperties => ({
  display: 'inline-block',
  width: 4,
  height: 16,
  backgroundColor: '#ff4d4f',
  borderRadius: 2,
  margin: '0 2px',
  animationName: 'waveAnim',
  animationDuration: '0.8s',
  animationTimingFunction: 'ease-in-out',
  animationIterationCount: 'infinite',
  animationDirection: 'alternate',
  animationDelay: delay,
});

const WaveAnimation: React.FC = () => (
  <>
    <style>{`
      @keyframes waveAnim {
        from { transform: scaleY(0.4); }
        to   { transform: scaleY(1.6); }
      }
    `}</style>
    <span style={{ display: 'inline-flex', alignItems: 'center', height: 24 }}>
      <span style={waveBarStyle('0s')} />
      <span style={waveBarStyle('0.15s')} />
      <span style={waveBarStyle('0.3s')} />
    </span>
  </>
);

// ─── Timer display ────────────────────────────────────────

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

// ─── Component ────────────────────────────────────────────

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onRecordingComplete,
  onTranscriptionComplete,
  autoTranscribe = false,
  preferBrowserRecognition = false,
  disabled = false,
}) => {
  const [recorderState, setRecorderState] = useState<RecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const speechRecognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const speechFinalTextRef = useRef('');
  const speechLatestTextRef = useRef('');
  const speechErrorRef = useRef(false);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioBlobRef = useRef<Blob | null>(null);

  const speechRecognitionConstructor = getSpeechRecognitionConstructor();
  const useBrowserRecognition = preferBrowserRecognition && !!speechRecognitionConstructor;
  const mediaRecorderSupported = typeof window !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined';
  const isSupported = useBrowserRecognition || mediaRecorderSupported;

  const stopTimer = useCallback((): void => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cleanupStream = useCallback((): void => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      stopTimer();
      cleanupStream();
      speechRecognitionRef.current?.abort();
      speechRecognitionRef.current = null;
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [stopTimer, cleanupStream, audioUrl]);

  const stopRecording = useCallback((): void => {
    if (speechRecognitionRef.current) {
      speechRecognitionRef.current.stop();
      stopTimer();
      return;
    }
    mediaRecorderRef.current?.stop();
    stopTimer();
    cleanupStream();
  }, [stopTimer, cleanupStream]);

  // Auto-stop at max duration
  useEffect(() => {
    if (recorderState === 'recording' && elapsed >= MAX_RECORD_SECONDS) {
      stopRecording();
    }
  }, [elapsed, recorderState, stopRecording]);

  const handleTranscribe = useCallback(async (blob: Blob): Promise<void> => {
    if (!autoTranscribe || !onTranscriptionComplete) return;
    setRecorderState('processing');
    try {
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');
      const res = await apiClient.post<{ data: { text: string } }>(
        '/m6/voice/transcribe',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const text = res.data.data.text;
      setTranscript(text);
      onTranscriptionComplete(text);
      setRecorderState('complete');
    } catch {
      setErrorMessage('转写失败，请检查网络或重试');
      setRecorderState('error');
    }
  }, [autoTranscribe, onTranscriptionComplete]);

  const startBrowserRecognition = useCallback((): void => {
    if (!speechRecognitionConstructor || !onTranscriptionComplete) {
      setErrorMessage('当前浏览器不支持直接语音识别，请改用文字输入');
      setRecorderState('error');
      return;
    }

    setErrorMessage(null);
    setTranscript(null);
    setElapsed(0);
    speechFinalTextRef.current = '';
    speechLatestTextRef.current = '';
    speechErrorRef.current = false;

    const recognition = new speechRecognitionConstructor();
    speechRecognitionRef.current = recognition;
    recognition.lang = navigator.language || 'zh-CN';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let finalText = '';
      let interimText = '';
      for (let index = 0; index < event.results.length; index += 1) {
        const result = event.results[index];
        const resultText = result?.[0]?.transcript ?? '';
        if (result?.isFinal) finalText += resultText;
        else interimText += resultText;
      }
      if (finalText) speechFinalTextRef.current = finalText;
      const latestText = (finalText || interimText).trim();
      speechLatestTextRef.current = latestText;
      setTranscript(latestText || null);
    };

    recognition.onerror = (event) => {
      speechErrorRef.current = true;
      const error = event.error;
      if (error === 'not-allowed' || error === 'service-not-allowed') {
        setErrorMessage('请允许浏览器麦克风和语音识别权限后重试');
      } else if (error === 'no-speech') {
        setErrorMessage('未识别到语音，请靠近麦克风后重试');
      } else {
        setErrorMessage('浏览器语音识别失败，请改用文字输入');
      }
      setRecorderState('error');
    };

    recognition.onend = () => {
      speechRecognitionRef.current = null;
      stopTimer();
      if (speechErrorRef.current) return;
      const finalText = (speechFinalTextRef.current || speechLatestTextRef.current).trim();
      if (!finalText) {
        setErrorMessage('未识别到完整语音，请重试');
        setRecorderState('error');
        return;
      }
      setTranscript(finalText);
      onTranscriptionComplete(finalText);
      setRecorderState('complete');
    };

    try {
      recognition.start();
      setRecorderState('recording');
      timerRef.current = setInterval(() => {
        setElapsed((previous) => previous + 1);
      }, 1000);
    } catch {
      speechRecognitionRef.current = null;
      setErrorMessage('浏览器语音识别启动失败，请重试');
      setRecorderState('error');
    }
  }, [onTranscriptionComplete, speechRecognitionConstructor, stopTimer]);

  const startRecording = async (): Promise<void> => {
    setErrorMessage(null);
    setAudioUrl(null);
    setTranscript(null);
    chunksRef.current = [];

    if (useBrowserRecognition) {
      startBrowserRecognition();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        audioBlobRef.current = blob;
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        const durationSeconds = elapsed;
        onRecordingComplete(blob, durationSeconds);

        if (autoTranscribe) {
          void handleTranscribe(blob);
        } else {
          setRecorderState('complete');
        }
      };

      recorder.start(250);
      setElapsed(0);
      setRecorderState('recording');

      timerRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setErrorMessage('请允许麦克风权限后重试');
      } else {
        setErrorMessage('麦克风初始化失败，请检查设备');
      }
      setRecorderState('error');
    }
  };

  const handleReset = (): void => {
    stopTimer();
    cleanupStream();
    speechRecognitionRef.current?.abort();
    speechRecognitionRef.current = null;
    speechFinalTextRef.current = '';
    speechLatestTextRef.current = '';
    speechErrorRef.current = false;
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setTranscript(null);
    setErrorMessage(null);
    setElapsed(0);
    setRecorderState('idle');
    audioBlobRef.current = null;
    chunksRef.current = [];
  };

  if (!isSupported) {
    return (
      <Alert
        type="warning"
        message="当前浏览器不支持录音功能，请使用 Chrome 或 Edge 最新版本"
        showIcon
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Error state */}
      {(recorderState === 'error' || errorMessage) && (
        <Alert
          type="error"
          message={errorMessage ?? '录音出错'}
          showIcon
          closable
          onClose={() => setErrorMessage(null)}
        />
      )}

      {/* Idle state */}
      {recorderState === 'idle' && (
        <Button
          icon={<AudioOutlined />}
          onClick={() => void startRecording()}
          disabled={disabled}
          size="middle"
        >
          {useBrowserRecognition ? '点击说话' : '点击录音'}
        </Button>
      )}

      {/* Recording state */}
      {recorderState === 'recording' && (
        <Space align="center">
          <WaveAnimation />
          <Text style={{ fontVariantNumeric: 'tabular-nums', color: '#ff4d4f', fontWeight: 600 }}>
            {formatTime(elapsed)}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {useBrowserRecognition ? '浏览器识别，录音不上传到本系统' : `最长 ${formatTime(MAX_RECORD_SECONDS)}`}
          </Text>
          <Button
            danger
            icon={<StopOutlined />}
            onClick={stopRecording}
            size="middle"
          >
            停止
          </Button>
        </Space>
      )}

      {/* Processing state */}
      {recorderState === 'processing' && (
        <Space>
          <Spin size="small" />
          <Text type="secondary">转写中...</Text>
        </Space>
      )}

      {/* Complete state */}
      {recorderState === 'complete' && (
        <Space direction="vertical" style={{ width: '100%' }}>
          {audioUrl && (
            <audio controls src={audioUrl} style={{ width: '100%', maxWidth: 360 }} />
          )}
          {transcript && (
            <div
              style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 6,
                padding: '8px 12px',
                fontSize: 13,
              }}
            >
              <Text type="success" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>
                转写结果:
              </Text>
              <Text>{transcript}</Text>
            </div>
          )}
          <Tooltip title="重新录音">
            <Button icon={<ReloadOutlined />} size="small" onClick={handleReset}>
              重录
            </Button>
          </Tooltip>
        </Space>
      )}
    </div>
  );
};

export default VoiceRecorder;
